import { logEvent } from "./logger";

/**
 * fetch with end-to-end tracing: mints an X-Request-ID (the Worker validates
 * and forwards it to the Go container; both log it), and emits one structured
 * log line per request. All API calls must go through this instead of bare
 * fetch, or their server-side log lines get an edge-minted ID that nothing in
 * the browser knows about.
 */

// Only the path is ever logged — query strings carry WS tickets.
function pathOf(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}

export async function tracedFetch(url: string, init?: RequestInit): Promise<Response> {
  const requestId = crypto.randomUUID();
  const method = init?.method ?? "GET";
  const headers = new Headers(init?.headers);
  headers.set("X-Request-ID", requestId);

  const path = pathOf(url);
  const start = performance.now();

  let res: Response;
  try {
    res = await fetch(url, { ...init, headers });
  } catch (e) {
    logEvent("error", "api_request_failed", {
      request_id: requestId,
      method,
      path,
      error: String(e),
      duration_ms: Math.round(performance.now() - start),
    });
    throw e;
  }

  logEvent(res.ok ? "debug" : "warn", "api_request", {
    request_id: requestId,
    method,
    path,
    status: res.status,
    duration_ms: Math.round(performance.now() - start),
  });
  return res;
}
